/** Custom-designed INR note / coin chips (no real currency imagery). */
export function RupeeChip({ denom, count, size = "md" }) {
  const isNote = denom.type === "note";
  const dims = {
    sm: isNote ? "w-[74px] h-[38px]" : "w-[38px] h-[38px]",
    md: isNote ? "w-[104px] h-[52px]" : "w-[52px] h-[52px]",
  }[size];

  return (
    <div
      data-testid={`denom-chip-${denom.value}`}
      className="flex items-center gap-3"
    >
      <div
        className={`relative ${dims} shrink-0 overflow-hidden ${
          isNote ? "rounded-[4px]" : "rounded-full"
        }`}
        style={{
          background: isNote
            ? `linear-gradient(135deg, ${denom.bg} 0%, ${shade(denom.bg, -14)} 100%)`
            : `radial-gradient(circle at 32% 28%, ${shade(denom.bg, 22)} 0%, ${denom.bg} 55%, ${shade(denom.bg, -22)} 100%)`,
          border: `1.5px solid ${shade(denom.bg, -30)}`,
          boxShadow: "0 1px 0 rgba(255,255,255,0.35) inset, 0 2px 6px rgba(15,23,42,0.18)",
        }}
      >
        {isNote && (
          <>
            <div
              className="absolute inset-[3px] rounded-[2px]"
              style={{ border: `1px dashed ${withAlpha(denom.ink, 0.35)}` }}
            />
            <div
              className="absolute inset-y-0 right-0 w-[18%]"
              style={{
                background: `repeating-linear-gradient(90deg, ${withAlpha(denom.ink, 0.18)} 0 2px, transparent 2px 5px)`,
              }}
            />
          </>
        )}
        {!isNote && (
          <div
            className="absolute inset-[4px] rounded-full"
            style={{ border: `1px solid ${withAlpha(denom.ink, 0.35)}` }}
          />
        )}
        <div
          className="absolute inset-0 flex items-center justify-center font-pos font-extrabold leading-none"
          style={{
            color: denom.ink,
            fontSize: isNote ? (size === "sm" ? 15 : 20) : size === "sm" ? 13 : 17,
            textShadow: "0 1px 0 rgba(255,255,255,0.25)",
          }}
        >
          ₹{denom.value}
        </div>
      </div>

      {count != null && (
        <div className="min-w-0">
          <div className="font-pos font-black text-slate-900 text-xl leading-none tabular-nums">
            × {count}
          </div>
          <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold mt-1">
            {isNote ? "Note" : "Coin"} · ₹{denom.value * count}
          </div>
        </div>
      )}
    </div>
  );
}

function hexToRgb(hex) {
  const h = hex.replace("#", "");
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
}

function shade(hex, amt) {
  const [r, g, b] = hexToRgb(hex).map((v) =>
    Math.max(0, Math.min(255, v + Math.round((amt / 100) * 255))),
  );
  return `rgb(${r},${g},${b})`;
}

function withAlpha(hex, a) {
  if (!hex.startsWith("#")) return hex;
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r},${g},${b},${a})`;
}
